import { orderStatusSchema, type OrderAdminDetail, type OrderStatus } from '@avrahami/contracts';
import {
  Alert,
  Button,
  buttonClass,
  Card,
  Cell,
  ConfirmDialog,
  formatDateTime,
  Loading,
  PageHeader,
  saveFile,
  SelectField,
  Table,
  uiWords,
  returnToPrevious,
} from '@avrahami/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { useCan } from '../api/session';
import { keys, orderQuery, tenantApi, useStoreTimeZone } from '../api/tenant';
import { QuoteCard } from '../components/QuoteCard';
import { OrderStatusBadge } from '../components/status';
import { he } from '../i18n/he';
import { errorMessage } from '../lib/forms';
import { orderDocumentUrl } from '../lib/media';
import { formatMoney } from '../lib/money';

function Facts({ rows }: { rows: readonly (readonly [string, ReactNode])[] }) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-slate-500">{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** One quote request: who asks, what for, where it stands, and its PDF. */
export function QuoteRequestDetailPage() {
  const { orderId } = useParams({ from: '/app/quote-requests/$orderId' });
  const order = useQuery(orderQuery(orderId));
  const canUpdate = useCan('store.orders.update');
  const timeZone = useStoreTimeZone();

  if (order.isPending) return <Loading />;
  if (order.isError) return <Alert>{errorMessage(order.error)}</Alert>;
  const detail = order.data;
  const address = detail.address;

  return (
    <>
      <Link
        to="/quote-requests"
        onClick={returnToPrevious}
        className="mb-2 inline-block text-sm text-brand-700 hover:underline"
      >
        {he.orders.back}
      </Link>
      <PageHeader
        title={`${he.orders.orderTitle} ${String(detail.number)}`}
        actions={<OrderDocument orderId={detail.id} number={detail.number} />}
      />
      <div className="grid gap-6 lg:grid-cols-2">
        <Card title={he.orders.status}>
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <OrderStatusBadge status={detail.status} />
              <span className="text-xs text-slate-500">
                {he.orders.placedAt} {formatDateTime(detail.createdAt, timeZone)}
              </span>
            </div>
            {canUpdate ? <OrderActions order={detail} /> : null}
          </div>
        </Card>

        <Card title={he.orders.contact}>
          <Facts
            rows={[
              [he.orders.name, detail.contact.fullName],
              [he.orders.email, <span className="ltr">{detail.contact.email}</span>],
              [he.orders.phone, <span className="ltr">{detail.contact.phone}</span>],
              [
                he.orders.customer,
                detail.customerId === null ? (
                  he.orders.guest
                ) : (
                  <Link
                    to="/customers/$customerId"
                    params={{ customerId: detail.customerId }}
                    data-testid="order-customer-link"
                    className="text-brand-700 hover:underline"
                  >
                    {he.orders.toCustomer}
                  </Link>
                ),
              ],
            ]}
          />
        </Card>

        {/* Only requests from when the form still asked how to receive the goods have this. */}
        {detail.fulfillment === null ? null : (
          <Card title={he.orders.delivery}>
            <Facts
              rows={[
                [
                  he.orders.fulfillment,
                  <span data-testid="order-fulfillment">{he.fulfillment[detail.fulfillment]}</span>,
                ],
                ...(address === null
                  ? []
                  : [
                      [
                        he.orders.address,
                        [
                          `${address.street} ${address.houseNumber}`,
                          address.apartment === null
                            ? null
                            : `${he.orders.apartment} ${address.apartment}`,
                          address.city,
                          address.zip === null ? null : `${he.orders.zip} ${address.zip}`,
                        ]
                          .filter((part) => part !== null)
                          .join(', '),
                      ] as const,
                    ]),
              ]}
            />
          </Card>
        )}

        <Card title={he.orders.notes}>
          <p className="whitespace-pre-line text-sm" data-testid="order-notes">
            {detail.notes ?? he.orders.noNotes}
          </p>
        </Card>
      </div>

      <div className="mt-6 flex flex-col gap-6">
        <Card title={he.orders.lines}>
          <Table
            head={[
              he.orders.product,
              he.orders.sku,
              he.orders.quantity,
              he.orders.unitPrice,
              he.orders.lineTotal,
            ]}
          >
            {detail.items.map((item) => (
              <tr key={item.productId} data-testid="order-line" data-sku={item.sku}>
                <Cell>{item.title}</Cell>
                <Cell className="ltr">{item.sku}</Cell>
                <Cell>{item.quantity}</Cell>
                <Cell className="whitespace-nowrap">{formatMoney(item.unitPriceMinor)}</Cell>
                <Cell className="whitespace-nowrap">{formatMoney(item.lineTotalMinor)}</Cell>
              </tr>
            ))}
          </Table>
          <Totals order={detail} />
        </Card>

        <QuoteCard
          orderId={detail.id}
          number={detail.number}
          canEdit={canUpdate}
          timeZone={timeZone}
        />

        <Card title={he.orders.history}>
          <ol className="flex flex-col gap-2 text-sm">
            {detail.history.map((entry, index) => (
              <li key={`${entry.changedAt}-${String(index)}`} className="flex flex-wrap gap-2">
                <span className="text-slate-500">{formatDateTime(entry.changedAt, timeZone)}</span>
                <OrderStatusBadge status={entry.status} />
                <span className="text-xs text-slate-500">
                  {he.orders.changedBy} {entry.changedBy ?? he.orders.byShopper}
                </span>
              </li>
            ))}
          </ol>
        </Card>
      </div>
    </>
  );
}

/** What the requested lines come to at the store's list prices; the shopper never saw it. */
function Totals({ order }: { order: OrderAdminDetail }) {
  return (
    <div className="mt-4 flex flex-col gap-3">
      <dl
        className="ms-auto grid w-full max-w-sm grid-cols-[1fr_auto] gap-x-6 gap-y-1 text-sm"
        data-testid="order-totals"
      >
        <dt className="font-semibold">{he.orders.grandTotal}</dt>
        <dd className="font-semibold" data-testid="order-total">
          {formatMoney(order.totalMinor)}
        </dd>
        <dt className="text-xs text-slate-500">
          {he.orders.vat} ({order.vatRatePercent}%)
        </dt>
        <dd className="text-xs text-slate-500">{formatMoney(order.vatMinor)}</dd>
      </dl>
      <p className="text-xs text-slate-500" data-testid="order-list-price-note">
        {he.orders.listPriceNote}
      </p>
    </div>
  );
}

/** Status moves: only those the API allows now. */
function OrderActions({ order }: { order: OrderAdminDetail }) {
  const queryClient = useQueryClient();
  const [next, setNext] = useState<OrderStatus | ''>('');
  const [confirmCancel, setConfirmCancel] = useState(false);
  const update = useMutation({
    mutationFn: (status: OrderStatus) =>
      tenantApi.updateOrder(order.id, { status, version: order.version }),
    onSuccess: async (updated) => {
      setNext('');
      setConfirmCancel(false);
      queryClient.setQueryData(keys.order(order.id), updated);
      await queryClient.invalidateQueries({ queryKey: keys.orders() });
      await queryClient.invalidateQueries({ queryKey: keys.dashboard() });
      await queryClient.invalidateQueries({ queryKey: keys.customers() });
    },
  });

  const apply = () => {
    if (next === '') return;
    if (next === 'CANCELLED') {
      setConfirmCancel(true);
      return;
    }
    update.mutate(next);
  };

  return (
    <div className="flex flex-col gap-3">
      {order.nextStatuses.length === 0 ? (
        <p className="text-sm text-slate-600">{he.orders.finalStatus}</p>
      ) : (
        <div className="flex flex-wrap items-end gap-2">
          <SelectField
            label={he.orders.newStatus}
            name="status"
            data-testid="order-status-select"
            disabled={update.isPending}
            value={next}
            onChange={(event) => {
              const value = event.target.value;
              setNext(value === '' ? '' : orderStatusSchema.parse(value));
            }}
          >
            <option value="">{he.orders.changeStatus}</option>
            {order.nextStatuses.map((status) => (
              <option key={status} value={status}>
                {he.orderStatus[status]}
              </option>
            ))}
          </SelectField>
          <Button
            data-testid="order-status-apply"
            disabled={next === '' || update.isPending}
            busy={update.isPending}
            onClick={apply}
          >
            {he.orders.applyStatus}
          </Button>
        </div>
      )}
      {update.isError ? <Alert>{errorMessage(update.error)}</Alert> : null}
      {update.isSuccess ? <Alert tone="success">{he.orders.updated}</Alert> : null}
      <ConfirmDialog
        open={confirmCancel}
        title={he.orders.cancelTitle}
        confirmLabel={he.orders.cancelConfirm}
        tone="danger"
        busy={update.isPending}
        onConfirm={() => {
          update.mutate('CANCELLED');
        }}
        onClose={() => {
          setConfirmCancel(false);
        }}
      >
        {he.orders.cancelBody}
      </ConfirmDialog>
    </div>
  );
}

/**
 * The quote request as a PDF. The link keeps its address (open in a new tab, copy it), but a click
 * fetches the file through the API client, so an expired session or a missing request shows a
 * message (and signs the member out where needed) instead of a browser error page.
 */
function OrderDocument({ orderId, number }: { orderId: string; number: number }) {
  const download = useMutation({
    mutationFn: () => tenantApi.orderDocument(orderId),
    onSuccess: (pdf) => {
      saveFile(pdf, `quote-request-${String(number)}.pdf`);
    },
  });
  return (
    <div className="flex flex-col items-end gap-1">
      <a
        href={orderDocumentUrl(orderId)}
        data-testid="order-document"
        aria-disabled={download.isPending}
        className={buttonClass('secondary')}
        onClick={(event) => {
          event.preventDefault();
          if (!download.isPending) download.mutate();
        }}
      >
        {download.isPending ? uiWords.loading : he.orders.document}
      </a>
      {download.isError ? <Alert>{errorMessage(download.error)}</Alert> : null}
    </div>
  );
}
